import type { UnitSystem } from "@/atoms/units";
import type { HourlyMarine } from "@/lib/marine";
import { fontStack } from "@/theme";
import { convertHeight, heightUnit } from "@/lib/units";
import type { HourlyWind } from "@/lib/weather";
import { findNearestWind, getWeatherCodeLabel } from "@/lib/weather";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";

type WindRelation = "offshore" | "cross" | "onshore";

/**
 * Classify wind relative to shore. Thresholds mirror conditions.ts scoring:
 * <60° = onshore (bad), >120° = offshore (good), between = cross (mixed).
 */
function classifyWind(windDir: number | null, shoreNormal: number | null): WindRelation {
  if (windDir == null || shoreNormal == null) return "cross";
  let diff = Math.abs(windDir - shoreNormal) % 360;
  if (diff > 180) diff = 360 - diff;
  if (diff < 60) return "onshore";
  if (diff > 120) return "offshore";
  return "cross";
}

/** Tiny direction glyph — rotates the arrow to the given compass bearing. */
function DirArrow({ deg, color }: { deg: number; color: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        transform: `rotate(${deg}deg)`,
        fontSize: 11,
        lineHeight: 1,
        color,
      }}
    >
      ↓
    </Box>
  );
}

interface HourlyForecastTableProps {
  hourly: HourlyMarine[];
  hourlyWind?: HourlyWind[];
  units: UnitSystem;
  shoreNormal?: number | null;
}

/**
 * Hourly breakdown — rule-divided table, no colored chips. Wind
 * onshore/cross/offshore shows as text color (buoy / ink.muted / kelp)
 * per DESIGN.md's "one accent, kelp/buoy sparingly" rule. All numbers
 * in mono with tabular-nums so columns snap to a grid.
 */
export default function HourlyForecastTable({
  hourly,
  hourlyWind = [],
  units,
  shoreNormal = null,
}: HourlyForecastTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const kelp = theme.palette.kelp.main;
  const buoy = theme.palette.buoy.main;
  const muted = theme.palette.ink.muted;
  const ink = theme.palette.ink.main;

  const WIND_TEXT_COLOR: Record<WindRelation, string> = {
    offshore: kelp,
    cross: muted,
    onshore: buoy,
  };

  const windByTime = new Map(hourlyWind.map((w) => [w.time, w]));

  // Mobile still strides by 2 so there's no scroll tax; desktop gets every hour.
  const stride = isMobile ? 2 : 1;
  const filtered = hourly.filter((_, i) => i % stride === 0);

  const unitLabel = heightUnit(units);
  const windUnit = units === "imperial" ? "mph" : "km/h";
  const windSpeedLabel = (w: HourlyWind) =>
    units === "imperial"
      ? (w.windSpeedKmh * 0.621371).toFixed(0)
      : w.windSpeedKmh.toFixed(0);

  // Header + row use the same grid so columns align. Fewer columns on
  // mobile: time / swell / wind / air. Desktop adds period and weather.
  const gridCols = {
    xs: "60px 1fr 1fr 48px",
    sm: "64px 1fr 1fr 1fr 1fr 64px",
  } as const;
  const headers = isMobile
    ? ["", "Swell", "Wind", "Air"]
    : ["", "Swell", "Period", "Wind", "Sky", "Air"];

  let prevDay = "";

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 1,
        }}
      >
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          Hourly
        </Typography>
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          Wind in {windUnit}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridCols,
          py: 1,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {headers.map((h, i) => (
          <Typography
            key={i}
            variant="overline"
            sx={{
              color: "ink.muted",
              textAlign: i === 0 ? "left" : "right",
              px: 0.5,
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>

      {/* Scroll container — the full 5-day hourly series is ~240 rows and
          blows out the page vertically. Cap it and let the user scroll
          within the table. Fades/sticky header stay out per DESIGN.md
          minimalism; the top/bottom rules already frame the list. */}
      <Box
        sx={{
          maxHeight: { xs: 420, sm: 520 },
          overflowY: "auto",
        }}
      >
        {filtered.map((h, i) => {
          const wind = windByTime.get(h.time) ?? findNearestWind(h.time, hourlyWind);
          const parsed = parseISO(h.time);
          const hour = parsed.getHours();
          const isNight = hour < 6 || hour >= 18;
          const dayKey = format(parsed, "yyyy-MM-dd");
          const isNewDay = dayKey !== prevDay;
          prevDay = dayKey;

          const windRel = classifyWind(wind?.windDirectionDeg ?? null, shoreNormal);
          const windColor = WIND_TEXT_COLOR[windRel];
          const ht = convertHeight(h.waveHeightM, units).toFixed(1);
          const periodStr = h.wavePeriod != null ? `${h.wavePeriod.toFixed(0)}s` : "—";
          const airTemp = wind
            ? units === "imperial"
              ? `${((wind.tempC * 9) / 5 + 32).toFixed(0)}°`
              : `${wind.tempC.toFixed(0)}°`
            : "—";

          return (
            <Box key={i}>
              {isNewDay && i > 0 && (
                <Typography
                  variant="overline"
                  component="div"
                  sx={{
                    color: "ink.muted",
                    pt: 2,
                    pb: 0.5,
                  }}
                >
                  {format(parsed, "EEEE, MMM d")}
                </Typography>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  alignItems: "center",
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  opacity: isNight ? 0.55 : 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fontStack.mono,
                    fontSize: "0.8125rem",
                    color: "ink.main",
                    pl: 0.5,
                  }}
                >
                  {format(parsed, "HH:mm")}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "baseline",
                    gap: 0.5,
                    px: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: fontStack.mono,
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      color: "ink.main",
                    }}
                  >
                    {ht}
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: fontStack.mono,
                        fontSize: "0.75rem",
                        color: "ink.muted",
                        ml: 0.25,
                      }}
                    >
                      {unitLabel}
                    </Typography>
                  </Typography>
                  <DirArrow deg={h.swellDirection ?? h.waveDirection} color={muted} />
                </Box>

                {!isMobile && (
                  <Typography
                    sx={{
                      fontFamily: fontStack.mono,
                      fontSize: "0.8125rem",
                      color: "ink.muted",
                      textAlign: "right",
                      px: 0.5,
                    }}
                  >
                    {periodStr}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "baseline",
                    gap: 0.5,
                    px: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: fontStack.mono,
                      fontSize: "0.8125rem",
                      color: windColor,
                    }}
                  >
                    {wind ? windSpeedLabel(wind) : "—"}
                  </Typography>
                  {wind && <DirArrow deg={wind.windDirectionDeg} color={windColor} />}
                </Box>

                {!isMobile && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "ink.muted",
                      textAlign: "right",
                      px: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {wind ? getWeatherCodeLabel(wind.weatherCode) : ""}
                  </Typography>
                )}

                <Typography
                  sx={{
                    fontFamily: fontStack.mono,
                    fontSize: "0.8125rem",
                    color: isNight ? muted : ink,
                    textAlign: "right",
                    px: 0.5,
                  }}
                >
                  {airTemp}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
